import { useId, type ReactNode } from 'react';
import { Alert, Card, CardBody, CardHeader, Text } from '@backstage/ui';
import { CodeSnippet, MarkdownContent } from '@backstage/core-components';
import { iconsApiRef, useApi } from '@backstage/frontend-plugin-api';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { CardTitle, CopyCommandButton } from '../ui';
import {
  type CreatedIcon,
  type CreatedRow,
  type CreationFacts,
  type InviteOutcome,
} from './creationTask';

/**
 * A row's icon, decorative (its element's name says the same): the official
 * logos of the icons registry (modules/icons.tsx) for GitLab and ArgoCD, the
 * ephemeral branches' ghost, the app's glyphs for the rest.
 */
function RowIcon(props: { icon: CreatedIcon }) {
  const iconsApi = useApi(iconsApiRef);
  switch (props.icon) {
    case 'gitlab':
    case 'argocd':
      return <>{iconsApi.icon(props.icon)}</>;
    case 'ephemeral':
      return <>{iconsApi.icon('branch-ephemeral')}</>;
    default: {
      const glyphs = {
        branch: waadooIcons.branch,
        lldap: waadooIcons.directoryGroup,
        catalog: waadooIcons.catalog,
      };
      const Glyph = glyphs[props.icon];
      return <Glyph aria-hidden focusable="false" />;
    }
  }
}

/**
 * « Ce qui a été créé »: the rows under the « Créés » pill and their count,
 * one chip per value with its muted note — the look of the deletion
 * report's groups (`.waadoo-deletion-report__group*`).
 */
function CreatedGroup(props: { rows: CreatedRow[] }) {
  const { rows } = props;
  const titleId = useId();
  return (
    <section
      className="waadoo-deletion-report__group"
      aria-labelledby={titleId}
    >
      <div className="waadoo-deletion-report__group-header">
        <span
          id={titleId}
          className="waadoo-deletion-report__pill"
          data-tone="success"
        >
          Créés
        </span>
        <span className="waadoo-deletion-report__group-count">
          {rows.length} élément(s)
        </span>
      </div>
      <ul className="waadoo-deletion-report__rows">
        {rows.map(row => (
          <li
            key={row.key}
            className="waadoo-deletion-report__row"
            data-row={row.key}
          >
            <span className="waadoo-deletion-report__row-icon" aria-hidden>
              <RowIcon icon={row.icon} />
            </span>
            <span className="waadoo-deletion-report__row-element">
              {row.element}
            </span>
            <span className="waadoo-deletion-report__chips">
              {row.items.map(item => (
                <span key={item.value} className="waadoo-deletion-report__chip">
                  {item.value}
                  {item.note && (
                    <span className="waadoo-service-row__note">
                      {' · '}
                      {item.note}
                    </span>
                  )}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The sentence above the git commands: the access the task gave. */
function accessSentence(invite: InviteOutcome): ReactNode {
  const ephemeral = (
    <>
      Chaque branche <code>dev-&lt;sujet&gt;</code> obtient son propre
      environnement de test en environ 3 minutes.
    </>
  );
  if (invite === 'member') {
    return (
      <>
        Vous êtes <strong>Developer</strong> du dépôt. {ephemeral}
      </>
    );
  }
  if (invite === 'pending') {
    return (
      <>
        Votre accès <strong>Developer</strong> sera effectif une fois
        l’invitation acceptée. {ephemeral}
      </>
    );
  }
  return ephemeral;
}

/**
 * « Compte rendu — <projet> » of a project creation (mockup `create-B`):
 * « Ce qui a été créé » (`createdRows`: only what a done step made); once
 * the task succeeded, « Travailler sur le code » — the access, the git
 * commands of the template's « Et maintenant ? » with the copy button, the
 * notes (mail capture, inviting a colleague) — and the template's
 * « Détails techniques », folded. After a failure, what was already created
 * only. A `gitlab-member` step whose log says the access was not granted
 * gets a warning pointing at the journal.
 */
export function CreationReport(props: {
  facts: CreationFacts;
  rows: CreatedRow[];
  invite: InviteOutcome;
  succeeded: boolean;
  /** The application's name (`taskTemplate.ts`). */
  appTitle: string;
}) {
  const { facts, rows, invite, succeeded, appTitle } = props;
  return (
    <Card>
      <CardHeader>
        <CardTitle level={3} title={<>Compte rendu — {facts.project}</>} />
      </CardHeader>
      <CardBody>
        <div className="waadoo-deletion-report__body">
          {invite === 'refused' && (
            <Alert
              status="warning"
              icon
              title="Votre accès au dépôt n’a pas pu être donné automatiquement"
              description="Le journal de l’étape « Votre accès » dit quoi demander à un administrateur."
            />
          )}

          <section>
            <Text as="h4" variant="body-large" weight="bold">
              {succeeded ? 'Ce qui a été créé' : 'Ce qui a déjà été créé'}
            </Text>
            {rows.length > 0 ? (
              <div className="waadoo-deletion-report__groups">
                <CreatedGroup rows={rows} />
              </div>
            ) : (
              <Text as="p" variant="body-medium" color="secondary">
                Rien n’a été créé.
              </Text>
            )}
          </section>

          {succeeded && (
            <section>
              <Text as="h4" variant="body-large" weight="bold">
                Travailler sur le code
              </Text>
              <Text as="p" variant="body-medium" color="secondary">
                {accessSentence(invite)}
              </Text>
              {facts.commands && (
                <div className="waadoo-deletion-report__code">
                  {/* Room on the right for the copy button, as on the
                      deletion report. */}
                  <CodeSnippet
                    text={facts.commands}
                    language="bash"
                    wrapLongLines
                    customStyle={{ paddingRight: '44px' }}
                  />
                  <CopyCommandButton text={facts.commands} />
                </div>
              )}
              <ul className="waadoo-deletion-report__notes waadoo-creation-report__notes">
                {facts.mailCatcher !== undefined && (
                  <li>
                    <Text as="span" variant="body-small" color="secondary">
                      {facts.mailCatcher
                        ? `Emails capturés : ceux qu’envoie ${appTitle} ne partent pas, ils sont dans l’onglet « Emails » de chaque branche.`
                        : `Capture des emails désactivée : ceux qu’envoie ${appTitle} partent réellement vers leurs destinataires.`}
                    </Text>
                  </li>
                )}
                <li>
                  <Text as="span" variant="body-small" color="secondary">
                    Pour inviter un collègue, demandez à un administrateur de
                    l’ajouter au projet, en lecture ou en édition.
                  </Text>
                </li>
              </ul>
            </section>
          )}

          {succeeded && facts.technical && (
            <details className="waadoo-deletion-report__details">
              <summary>Détails techniques</summary>
              <MarkdownContent content={facts.technical} />
            </details>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
